import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/apiConfig';
import RiskAssessmentReport from './components/RiskAssessmentReport';
import { Button, Dropdown, DropdownItem } from '../../components/ui';
import { ArrowLeftIcon, PrinterIcon } from '../../components/ui/Icons';
import { exportRiskAssessmentToExcel } from './utils/exportRiskAssessmentToExcel';

/**
 * RiskAssessmentPrintPage - Standalone page for printing risk assessment reports
 * Accessible via /risk-assessment/:id/print
 */
const RiskAssessmentPrintPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [riskAssessment, setRiskAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchRiskAssessment = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        // Fetch risk assessment
        const response = await fetch(`${API_BASE_URL}/risk-assessment/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch risk assessment');
        }

        const data = await response.json();
        
        // Fetch related timeline entries (actions are already included in the main response)
        const timelineRes = await fetch(`${API_BASE_URL}/risk-assessment-timeline-entry/by-risk-assessment/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        }).catch(() => ({ ok: false, json: () => [] }));

        const timelineEntries = timelineRes.ok ? await timelineRes.json() : [];

        // Merge the data
        setRiskAssessment({
          ...data,
          riskAssessmentActions: data.riskAssessmentActions || [],
          riskAssessmentTimeLineEntries: timelineEntries
        });
      } catch (err) {
        console.error('Error fetching risk assessment:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRiskAssessment();
    }
  }, [id]);

  const handlePrint = () => {
    // Small delay to ensure content is fully rendered
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Auto-print when page loads (optional - can be removed if not desired)
  useEffect(() => {
    if (riskAssessment && !loading && !error) {
      // Auto-print after a short delay to ensure content is rendered
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [riskAssessment, loading, error]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleExportToExcel = async () => {
    if (!riskAssessment) return;
    
    try {
      setExporting(true);
      await exportRiskAssessmentToExcel(riskAssessment);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert(`Failed to export to Excel: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading risk assessment data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  if (!riskAssessment) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="mb-4">Risk assessment not found</p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="risk-assessment-report-wrapper min-h-screen bg-gray-100">
      {/* Print Controls - Hidden when printing */}
      <div className="no-print bg-white border-b shadow-sm p-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={handleBack} variant="outline" size="sm">
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-semibold">Risk Assessment Report - {riskAssessment.code}</h1>
          </div>
          <Dropdown
            trigger={
              <Button
                variant="primary"
                size="sm"
                disabled={exporting}
                title="Print and Export Options"
              >
                <PrinterIcon className="w-4 h-4 mr-2" />
                Print Options
              </Button>
            }
            align="right"
            className="w-64"
          >
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
              Print Options
            </div>
            <DropdownItem 
              onClick={handlePrint}
              className="flex items-center gap-3 px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex-shrink-0">
                <PrinterIcon className="w-5 h-5" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Print Report</span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Print to PDF or printer</span>
              </div>
            </DropdownItem>
            <DropdownItem 
              onClick={handleExportToExcel}
              disabled={exporting}
              className="flex items-center gap-3 px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex-shrink-0">
                {exporting ? (
                  <div className="w-5 h-5 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {exporting ? 'Exporting...' : 'Export to Excel'}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Microsoft Excel format</span>
              </div>
            </DropdownItem>
          </Dropdown>
        </div>
      </div>

      {/* Report Content */}
      <div className="py-4" style={{ minHeight: 'auto', height: 'auto', overflow: 'visible', background: 'white' }}>
        {riskAssessment && <RiskAssessmentReport riskAssessment={riskAssessment} />}
      </div>
    </div>
  );
};

export default RiskAssessmentPrintPage;

